import { Component, OnInit, Optional } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { firstValueFrom, Subscription } from 'rxjs';
import { InventoryClassModel } from 'src/app/models/inventory/inventory-class.model';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';
import { InventoryClassService } from '../../../../services/inventory/inventory-class.service';

@Component({
  selector: 'app-register-inventory-class',
  templateUrl: './register-inventory-class.component.html',
  styleUrls: ['./register-inventory-class.component.scss'],
  standalone: false
})
export class RegisterInventoryClassComponent implements OnInit {
  inventoryClassContract: InventoryClassModel = new InventoryClassModel;
  inventoryClassId: number = 0;
  formInventoryClass: FormGroup = new FormGroup({});
  subscription: Subscription;

  constructor(public config: DynamicDialogConfig,
    private formBuilder: FormBuilder,
    @Optional() private ref: DynamicDialogRef,
    private toastHandlerService: ToastHandlerService,
    private inventoryClassService: InventoryClassService) { }

  ngOnInit(): void {
    const input$ = this.config.data?.inputs$;
    if (!input$) {
      console.error('inputs$ no está definido en config.data');
      return;
    }
    this.subscription = input$?.subscribe((inputs: any) => {
      this.inventoryClassContract = inputs.inventoryClassModel;
      if (this.inventoryClassContract.inventoryClassId != 0) {
        this.inventoryClassId = this.inventoryClassContract.inventoryClassId;
      }
      this.validateForm(new InventoryClassModel());
      this.handleInitialState();
    });
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  changeInventoryClass() {
    const code = this.formInventoryClass.get("code")?.value;

    if (code != 0) {
      this.getInventoryClassByCode(code);
    } else {
      this.createNewInventoryClass();
    }
  }

  createNewInventoryClass() {
    this.inventoryClassId = 0;
    this.inventoryClassContract = new InventoryClassModel();
    this.validateForm(this.inventoryClassContract);
    setTimeout(() => this.formInventoryClass.controls["code"].disable(), 100);
  }

  async getInventoryClassByCode(code: number) {
    try {
      this.inventoryClassContract = await firstValueFrom(this.inventoryClassService.getInventoryClassByCode(code));
      this.inventoryClassId = this.inventoryClassContract.inventoryClassId;
      this.validateForm(this.inventoryClassContract);
    } catch (error) {
      console.error('Error al buscar la clase de inventario por código.', error);
    }
  }

  async getInventoryClassById() {
    try {
      this.inventoryClassContract = await firstValueFrom(this.inventoryClassService.getInventoryClassById(this.inventoryClassId));
      this.inventoryClassId = this.inventoryClassContract.inventoryClassId;
      await this.validateForm(this.inventoryClassContract);
    } catch (error) {
      console.error('Error al buscar la clase inventario por id.', error);
    }
  }

  async handleInitialState() {
    if (this.inventoryClassId !== null && this.inventoryClassId !== 0) {
      await this.getInventoryClassById();
    } else {
      this.createNewInventoryClass();
    }
  }

  async send() {
    Object.keys(this.formInventoryClass.controls).forEach(key => {
      const control = this.formInventoryClass.get(key);
      if (control && control.invalid) {
        console.warn(`${key} es inválido`, control.errors, 'value:', control.value);
      }
    });

    if (this.formInventoryClass.valid) {
      const modelInventoryClass = this.formInventoryClass.getRawValue();

      if (this.inventoryClassId == 0) {
        try {
          const response = await firstValueFrom(this.inventoryClassService.createInventoryClass(modelInventoryClass));
          this.toastHandlerService.showToast('success', `La clase de inventario fue creada exitosamente.`);
          this.inventoryClassContract = response;
          this.inventoryClassId = this.inventoryClassContract.inventoryClassId;
          this.validateForm(this.inventoryClassContract);
        } catch (error) {
          console.error('Error al crear la clase de inventario.', error);
        }
      } else {
        this.inventoryClassContract.name = modelInventoryClass.name;
        try {
          const response = await firstValueFrom(this.inventoryClassService.updateInventoryClass(this.inventoryClassContract));
          this.toastHandlerService.showToast('success', `La clase de inventario fue modificada exitosamente.`);
          this.inventoryClassContract = response;
          this.validateForm(this.inventoryClassContract);
        } catch (error) {
          console.error('Error al actualizar la clase de inventario.', error);
        }
      }
      const output$ = this.config.data?.output$;
      output$?.next({ type: 'reload' });
    } else {
      this.showValidationMessages();
    }
  }

  showValidationMessages() {
    const controlMessages: Record<string, Record<string, string>> = {
      code: {
        required: 'La código es obligatirio.',
        maxlength: 'El campo código permite máximo 3 caracteres.'
      },
      name: {
        required: 'El nombre es obligatorio.',
        maxlength: 'El campo nombre permite máximo 150 caracteres.'
      }
    };

    Object.keys(controlMessages).forEach(controlName => {
      const control = this.formInventoryClass.get(controlName);
      if (control && control.invalid && control.errors) {
        const errors = control.errors;
        Object.keys(errors).forEach(errorKey => {
          const message = controlMessages[controlName][errorKey];
          if (message) {
            this.toastHandlerService.showToast('warning', message, 'Atención');
          }
        });
      }
    });
  }

  validateForm(inventoryClass: InventoryClassModel) {
    this.formInventoryClass = this.formBuilder.group({
      code: [inventoryClass.code, [Validators.required]],
      name: [inventoryClass.name, [Validators.required, Validators.maxLength(150)]]
    });
  }
}
